// import BetWarningBanner from '@/components/atoms/betWarningBanner/betWarningBanner'
import { useEffect, useMemo, useState } from 'react'

import {
    noProductsImg,
    vendorPlaceholderDark,
    welcome_gaming_playStation
} from '@/assets/images'
import PrimaryButton from '@/components/atoms/primaryButton/primaryButton'
import AcceptedBills from '@/components/molecules/acceptedBills/acceptedBills'
import VendorBonusAmountNote from '@/components/molecules/vendorBonusAmountNote/vendorBonusAmountNote'
import VendorBonusProgressBanner from '@/components/organisms/vendorBonusProgressBanner/vendorBonusProgressBanner'
import { useTranslate } from '@/i18n/useTranslate'
import { useOrder } from '@/providers'
import type { VendorBonusClaim } from '@/types/pos/depositBonus'
import {
    isTestCashDepositEnabled,
    simulateTestDeposit,
    TEST_DEPOSIT_AMOUNT
} from '@/utilities/testDepositHelper'

export type InsertingCashProps = {
    amount: number
    onProcessPayment: () => void
    onUsePreviousVoucher: () => void
    canUsePreviousVoucher: boolean
    amountByDraftDepositType?: Record<string, number>
    showNextPageHint?: boolean
    accountId?: string
    onChangeSelection?: () => void
    vendorBonusClaim?: VendorBonusClaim | null
}

export default function InsertingCash({
    amount,
    onProcessPayment,
    onUsePreviousVoucher,
    canUsePreviousVoucher,
    showNextPageHint = false,
    accountId,
    onChangeSelection,
    vendorBonusClaim
}: InsertingCashProps) {
    const { t } = useTranslate()
    const { state } = useOrder()
    const showTestDeposit = isTestCashDepositEnabled()
    const [isSimulatingDeposit, setIsSimulatingDeposit] = useState(false)
    const isGaming = state.voucherType === 'gaming'
    const isBetting = state.voucherType === 'betting'
    const selectedVendor = state.selectedBettingVendor
    const basketTitleKey = isBetting
        ? 'cardPayment.selectedService'
        : 'cardPayment.selectedProduct'
    const showDirectBettingBasket = Boolean(selectedVendor && accountId)
    const showSelectedProductCard = isGaming || showDirectBettingBasket

    const basketSummary = useMemo(() => {
        return Object.values(state.basket).reduce(
            (acc, item) => {
                acc.totalQty += item.qty
                acc.totalPrice += (item.product.price || 0) * item.qty
                return acc
            },
            { totalQty: 0, totalPrice: 0 }
        )
    }, [state.basket])

    const amountDifference = basketSummary.totalPrice - amount
    const isChange = amountDifference < 0
    const differenceValue = Math.abs(amountDifference)
    const progressPercent =
        basketSummary.totalPrice > 0
            ? Math.min((amount / basketSummary.totalPrice) * 100, 100)
            : 0

    const basketItems = Object.values(state.basket)
    const selectedProduct = basketItems.length > 0 ? basketItems[0].product : null
    const showBonusBanner = Boolean(vendorBonusClaim)
    const showBonusOnAmount = Boolean(vendorBonusClaim && amount > 0)

    const handleSimulateDeposit = async () => {
        if (isSimulatingDeposit) return

        setIsSimulatingDeposit(true)
        try {
            await simulateTestDeposit()
        } catch (error) {
            console.error('Failed to simulate test deposit', error)
        } finally {
            setIsSimulatingDeposit(false)
        }
    }

    useEffect(() => {
        console.log('InsertingCash basket state:', state.basket)
    }, [state.basket])

    return (
        <div
            className={`insert-cash insert-cash--new-layout ${showBonusBanner ? 'insert-cash--with-bonus' : ''}`}>
            <h1>{t('insertCash.title')}</h1>
            
            <AcceptedBills />

            {vendorBonusClaim ? (
                <VendorBonusProgressBanner claim={vendorBonusClaim} />
            ) : null}

            <div
                className={`insert-cash__content-grid ${!showSelectedProductCard ? 'insert-cash__content-grid--single' : ''}`}>
                {/* Left side - Selected product or direct betting details */}
                {showSelectedProductCard && (
                    <div className="insert-cash__products-card">
                        <h2>{t(basketTitleKey)}</h2>

                        {showDirectBettingBasket && selectedVendor ? (
                            <div className="insert-cash__vendor-summary">
                                <div className="insert-cash__vendor-logo">
                                    <img
                                        src={
                                            selectedVendor.squareImageUrl ||
                                            vendorPlaceholderDark
                                        }
                                        alt={selectedVendor.name}
                                    />
                                </div>
                                <h3>{selectedVendor.name}</h3>
                                <div className="insert-cash__vendor-account">
                                    <span>{t('cardPayment.accountNumber')}</span>
                                    <strong>{accountId}</strong>
                                </div>
                                <button
                                    type="button"
                                    className="insert-cash__change-selection"
                                    onClick={onChangeSelection}>
                                    {t('bettingVendorAccount.changeSelection')}
                                </button>
                            </div>
                        ) : selectedProduct ? (
                            <>
                                <div className="insert-cash__product-image-large">
                                    <img
                                        src={
                                            selectedProduct.imageUrl ||
                                            welcome_gaming_playStation
                                        }
                                        alt={selectedProduct.name}
                                    />
                                </div>
                                <div className="insert-cash__product-details-large">
                                    <h3>{selectedProduct.name}</h3>
                                    {selectedProduct.price && (
                                        <p className="insert-cash__product-price-large">
                                            {selectedProduct.price.toFixed(0)} RSD
                                        </p>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="insert-cash__empty-products">
                                <div className="insert-cash__empty-icon">
                                    <img
                                        src={noProductsImg}
                                        alt={t('insertCash.noSelectedProducts')}
                                    />
                                </div>
                                <p className="insert-cash__empty-text">
                                    {t('insertCash.noSelectedProducts')}
                                </p>
                                <p className="insert-cash__empty-subtext">
                                    {t('insertCash.canTopUpAmount')}
                                </p>
                            </div>
                        )}

                        {selectedProduct && (
                            <div className="insert-cash__product-total">
                                <span>{t('insertCash.total')}:</span>
                                <strong>
                                    {basketSummary.totalPrice.toFixed(0)} RSD
                                </strong>
                            </div>
                        )}
                        {selectedProduct && (
                            <button
                                type="button"
                                className="insert-cash__change-selection"
                                onClick={onChangeSelection}>
                                {t('bettingVendorAccount.changeSelection')}
                            </button>
                        )}
                    </div>
                )}

                {/* Right side - Payment Info */}
                <div
                    className={`insert-cash__payment-card ${!showSelectedProductCard ? 'insert-cash__payment-card--full' : ''} ${showBonusOnAmount ? 'insert-cash__payment-card--with-bonus' : ''}`}>
                    <div className="insert-cash__paid-label">
                        {t(
                            showBonusOnAmount
                                ? 'insertCash.insertedSoFar'
                                : 'insertCash.insertedAmount'
                        )}
                        :
                    </div>
                    <div className="insert-cash__paid-value">
                        {amount.toFixed(2)} <span>RSD</span>
                    </div>

                    {showBonusOnAmount && vendorBonusClaim ? (
                        <VendorBonusAmountNote
                            vendorName={vendorBonusClaim.vendorName}
                            bonusAmount={vendorBonusClaim.rewardAmount}
                            currencyCode={vendorBonusClaim.currencyCode}
                            depositOrdinal={vendorBonusClaim.depositOrdinal}
                            paymentAmount={amount}
                        />
                    ) : null}
                    
                    {selectedProduct && basketSummary.totalPrice > 0 && (
                        <>
                            <div className="insert-cash__progress">
                                <div
                                    className="insert-cash__progress-bar"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <div className="insert-cash__remaining">
                                <span>
                                    {isChange
                                        ? t('insertCash.change')
                                        : t('insertCash.remaining')}:
                                </span>
                                <strong>
                                    {differenceValue.toFixed(0)} RSD
                                </strong>
                            </div>
                        </>
                    )}
                    
                    {canUsePreviousVoucher && (
                        <button
                            className="insert-cash__has-voucher"
                            onClick={onUsePreviousVoucher}>
                            {t('insertCash.hasVoucher')}
                        </button>
                    )}

                    {showNextPageHint && (
                        <p className="insert-cash__next-page-note">
                            {t('insertCash.nextPageConfirmPayment')}
                        </p>
                    )}
                </div>
            </div>

            <div className="buttons-wrapper">
                {showTestDeposit ? (
                    <button
                        type="button"
                        className="insert-cash__simulate-deposit"
                        disabled={isSimulatingDeposit}
                        onClick={() => {
                            void handleSimulateDeposit()
                        }}>
                        {isSimulatingDeposit
                            ? 'Simulating deposit…'
                            : `Simulate deposit (${TEST_DEPOSIT_AMOUNT} RSD)`}
                    </button>
                ) : null}
                <PrimaryButton
                    isDisabled={!amount || amount < (isGaming ? 200 : 500)}
                    text={
                        amount < (isGaming ? 200 : 500)
                            ? t('insertCash.minAmount') +
                              ' ' +
                              (isGaming ? '200 RSD' : '500 RSD')
                            : t('insertCash.confirmPayment')
                    }
                    callback={onProcessPayment}
                />
            </div>
        </div>
    )
}
